import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronLeftIcon, IndianRupeeIcon } from "lucide-react";
import { Oval } from "react-loader-spinner";
import PageLayout from "@/components/custom/PageLayout";
import ErrorMessageFullCard from "@/components/custom/ErrorMessageFullCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SuccessTickIcon from "@/assets/icons/SuccessTickIcon.svg";
import CarLeft from "@/assets/illustrations/CarLeft.png";
import { RootState, useAppDispatch } from "@/redux/store";
import { fetchDriverDetails } from "@/redux/driverDetailsSlice";
import EverestTopupService from "@/services/api/EverestTopupService";
import {
  CreateEverestTopupLinkResponse,
  EverestTopupTxn,
} from "@/central-atoms/everest-fleet-pwa/responses";
import { useTheme } from "@/themeConfigs/ThemeContext";
import i18n from "@/lang/i18n";

/** How often the order state is asked for while the driver is on the Razorpay page. */
const STATUS_POLL_INTERVAL_MS = 3000;

/** Give up waiting for the order after this long; the driver can retry from the history list. */
const STATUS_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const MAX_AMOUNT = 50000;

/**
 * Driver top-up through Everest's Razorpay account (`everest_checkin_apis` drivers only).
 *
 * The outstanding balance from `driver_details.topup` prefills the amount, but the driver can
 * enter any amount. Pay opens Everest's Razorpay link in a new tab and this screen polls the
 * order state; once Everest reports it paid, the home screen is refetched so the readiness
 * status (OS pending -> ready) is current the moment the driver returns.
 */
const EverestTopup: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();

  const topup = useSelector(
    (state: RootState) => state.driverDetails.details?.data?.topup
  );

  const [amount, setAmount] = useState<string>(
    topup?.outstanding_amount ? `${topup.outstanding_amount}` : ""
  );
  const [amountError, setAmountError] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTopup, setActiveTopup] = useState<EverestTopupTxn | null>(null);
  const [history, setHistory] = useState<EverestTopupTxn[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number>(STATUS_POLL_TIMEOUT_MS / 1000);
  const pollTimer = useRef<number | null>(null);
  const pollStartedAt = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const response = await EverestTopupService.getTopupHistory();
      setHistory(response?.data || []);
    } catch (err) {
      // History is informational; the top-up itself does not depend on it.
    }
  }, []);

  useEffect(() => {
    loadHistory();
    return () => stopPolling();
  }, [loadHistory, stopPolling]);

  // Once the order is settled, pull the fresh readiness / button state before the driver goes
  // back: Everest may have cleared the outstanding balance and the home screen must show it.
  const handlePaid = useCallback(async () => {
    stopPolling();
    await dispatch(fetchDriverDetails({ force: true }));
    loadHistory();
  }, [dispatch, loadHistory, stopPolling]);

  const pollStatus = useCallback(
    async (orderId: string) => {
      try {
        const response = await EverestTopupService.getTopupStatus(orderId);
        const txn = response?.data;
        if (txn) {
          setActiveTopup(txn);
          if (txn.payment_status === "complete") {
            await handlePaid();
            return;
          }
          if (txn.payment_status === "failed") {
            stopPolling();
            loadHistory();
            return;
          }
        }
      } catch (err) {
        // A transient status failure is not a payment failure; keep polling until the timeout.
      }

      const elapsed = Date.now() - pollStartedAt.current;
      setSecondsLeft(Math.max(0, Math.round((STATUS_POLL_TIMEOUT_MS - elapsed) / 1000)));
      if (elapsed >= STATUS_POLL_TIMEOUT_MS) {
        stopPolling();
        setActiveTopup((current) =>
          current
            ? {
                ...current,
                payment_status: "failed",
                message:
                  "We did not get a payment confirmation in time. If money was deducted it will be refunded in 5-7 days; otherwise please try again.",
              }
            : current
        );
        return;
      }

      pollTimer.current = window.setTimeout(() => pollStatus(orderId), STATUS_POLL_INTERVAL_MS);
    },
    [handlePaid, loadHistory, stopPolling]
  );

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    setAmountError(null);
  };

  const handlePay = async () => {
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 1 || parsedAmount > MAX_AMOUNT) {
      setAmountError(`Enter an amount between 1 and ${MAX_AMOUNT}.`);
      return;
    }

    setIsCreating(true);
    try {
      const response = await EverestTopupService.createTopupLink({ amount: parsedAmount });
      const txn = response?.data?.details;
      if (!response?.success || !txn?.payment_url) {
        setFatalError(response?.data?.messages || [i18n.t("topup_error")]);
        return;
      }

      setActiveTopup(txn);
      // Open the Razorpay page first: it must be a direct result of the tap or the browser blocks it.
      window.open(txn.payment_url, "_blank");
      pollStartedAt.current = Date.now();
      setSecondsLeft(STATUS_POLL_TIMEOUT_MS / 1000);
      pollTimer.current = window.setTimeout(() => pollStatus(txn.order_id), STATUS_POLL_INTERVAL_MS);
    } catch (err: any) {
      const errorData = err?.response?.data as CreateEverestTopupLinkResponse | undefined;
      setFatalError(errorData?.data?.messages || [i18n.t("topup_error")]);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRetry = () => {
    stopPolling();
    setActiveTopup(null);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const statusLabel = (status: EverestTopupTxn["payment_status"]) => {
    if (status === "complete") {
      return { text: i18n.t("topup_paid"), className: "text-[#4cb726]" };
    }
    if (status === "failed") {
      return { text: i18n.t("topup_failed"), className: "text-[#fa0404]" };
    }
    return { text: i18n.t("topup_pending"), className: "text-[#d9aa06]" };
  };

  if (fatalError.length > 0) {
    return (
      <PageLayout noPad>
        <div className="h-full relative">
          <ErrorMessageFullCard
            errorString={fatalError}
            handleBackToHome={() => navigate("/driver-home")}
          />
        </div>
      </PageLayout>
    );
  }

  if (activeTopup?.payment_status === "complete") {
    return (
      <PageLayout bgColor="bg-[#e4fde5] items-center">
        <div className="flex flex-col items-center justify-center">
          <img src={SuccessTickIcon} alt="Success" />
          <p className="text-3xl text-center font-bold">{i18n.t("topup_successful")}</p>
          <p className="text-xl text-center font-semibold mt-2">₹{activeTopup.amount}</p>
          <p className="text-center text-sm mt-4 px-6">{activeTopup.message}</p>
          <Button
            variant="secondary"
            className="w-9/12 mt-6"
            onClick={() => navigate("/driver-home")}
          >
            {i18n.t("back_to_home")}
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (activeTopup) {
    const isFailed = activeTopup.payment_status === "failed";
    return (
      <PageLayout noPad>
        <div className="absolute bg-white w-full h-full z-20 flex items-center justify-center px-[24px]">
          <div
            className={`relative flex flex-col items-center justify-center w-full min-h-[176px] border rounded-[30px] p-[24px] pt-[20px] ${
              isFailed
                ? "border-[#fa0404] shadow-[0px_2px_0px_#fa0404] bg-[#fff5f5]"
                : "border-primary shadow-[0px_2px_0px_#3ebec8] bg-white"
            }`}
          >
            <p className="w-full pb-2 text-lg text-center">
              {isFailed ? i18n.t("topup_failed") : i18n.t("topup_complete_on_razorpay")}
            </p>
            <p className="w-full py-2 text-4xl font-extrabold text-center">₹{activeTopup.amount}</p>
            <p className="text-center text-sm text-secondary px-2">{activeTopup.message}</p>
            {!isFailed && (
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  className="shadow-2xl mt-3"
                  onClick={() => window.open(activeTopup.payment_url, "_blank")}
                >
                  {i18n.t("topup_open_razorpay")}
                </Button>
                <div className="flex items-center gap-2 mt-4">
                  <Oval visible height={18} width={18} color="#3ebec8" secondaryColor="#e2e8f0" strokeWidth={6} />
                  <p className={`text-center text-sm ${secondsLeft <= 60 ? "text-red-600" : ""}`}>
                    {i18n.t("topup_waiting")} {formatTime(secondsLeft)}
                  </p>
                </div>
              </>
            )}
            <Button
              variant={isFailed ? "secondary" : "ghost"}
              className="w-full mt-4"
              onClick={isFailed ? handleRetry : () => navigate("/driver-home")}
            >
              {isFailed ? i18n.t("topup_try_again") : i18n.t("back_to_home")}
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout noPad>
      <div className="bg-secondary">
        <div
          className={`w-full flex ${
            theme.layout.backButton ? "justify-between" : "justify-end"
          } pt-10`}
        >
          {theme.layout.backButton && (
            <Button
              variant="icon"
              className="text-white bg-secondary rounded-xl border-2 border-white hover:shadow-lg px-2 py-6 ml-4 mb-6"
              onClick={() => navigate("/driver-home")}
            >
              <ChevronLeftIcon strokeWidth={3} size={28} />
            </Button>
          )}
        </div>
        <div className="w-full flex justify-start">
          <img src={CarLeft} className="w-1/2" />
        </div>
        <p className="flex flex-row justify-center text-4xl text-white py-10">
          {i18n.t("topUp")}
        </p>
      </div>
      <div className="bg-white px-7 pb-8">
        <div className="flex flex-col">
          {topup?.outstanding_amount ? (
            <div className="my-2 rounded-xl border border-[#d9aa06] bg-[#fffbef] px-4 py-3">
              <p className="text-sm text-[#d9aa06]">{i18n.t("topup_outstanding")}</p>
              <p className="text-2xl font-bold text-secondary">₹{topup.outstanding_amount}</p>
            </div>
          ) : null}
          <div className="my-2">
            <Label className="block text-sm font-bold py-2" htmlFor="topup-amount">
              {i18n.t("topup_enter_amount")}
            </Label>
            <Input
              startIcon={IndianRupeeIcon}
              variant="primary"
              type="number"
              inputMode="decimal"
              id="topup-amount"
              min={1}
              max={MAX_AMOUNT}
              value={amount}
              onChange={handleAmountChange}
            />
            {amountError && <p className="text-red-600 text-sm mt-2">{amountError}</p>}
          </div>
          <Button
            className="mt-5"
            variant="secondary"
            size="lg"
            onClick={handlePay}
            disabled={isCreating || !amount}
          >
            {isCreating ? i18n.t("processing") : i18n.t("topup_pay_now")}
          </Button>

          {history.length > 0 && (
            <div className="mt-8">
              <p className="text-sm font-bold py-2">{i18n.t("topup_recent")}</p>
              <div className="flex flex-col divide-y divide-gray-200 border border-gray-200 rounded-xl">
                {history.map((txn) => {
                  const label = statusLabel(txn.payment_status);
                  return (
                    <div key={txn.payment_txn_id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="font-semibold text-secondary">₹{txn.amount}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(txn.created_on).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <p className={`text-sm font-semibold ${label.className}`}>{label.text}</p>
                        {txn.payment_status === "pending" && txn.payment_url && (
                          <button
                            type="button"
                            className="text-xs text-primary underline"
                            onClick={() => {
                              setActiveTopup(txn);
                              window.open(txn.payment_url, "_blank");
                              pollStartedAt.current = Date.now();
                              setSecondsLeft(STATUS_POLL_TIMEOUT_MS / 1000);
                              pollTimer.current = window.setTimeout(() => pollStatus(txn.order_id), STATUS_POLL_INTERVAL_MS);
                            }}
                          >
                            {i18n.t("topup_resume")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default EverestTopup;
